import {
    Workflow,
    WorkflowAction,
    WorkflowAction_Limits,
    WorkflowStep,
    WorkflowStep_PullRequest,
    WorkflowTrigger,
    WorkflowTrigger_Manual,
    WorkflowTrigger_PullRequest,
    WorkflowTrigger_PullRequestEvent,
    WorkflowTriggerContext,
    WorkflowTriggerContext_Projects,
} from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { CentralIconBaseProps } from "central-icons/CentralIconBase";
import IconFocusAuto from "central-icons/IconFocusAuto";
import IconShieldCheck3 from "central-icons/IconShieldCheck3";
import IconFileText from "central-icons/IconFileText";
import IconCodeAnalyze from "central-icons/IconCodeAnalyze";
import { DEFAULT_WORKFLOW_LIMITS } from "@/utils/automations";
import { AutomationsCapabilities } from "@/hooks/use-feature-automations";
import IconFileBend from "central-icons/IconFileBend";
import IconRobot from "central-icons/IconRobot";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";

// Explicitly not exported
const AUTOMATION_TEMPLATES_INTERNAL = {
    "start-from-scratch": {
        id: "start-from-scratch",
        title: "Start from scratch",
        description: "Create a custom automation workflow from scratch.",
        icon: IconFileBend,
        iconColor: "text-content-brand",
        iconBgColor: "bg-surface-brand-subtle",
        enabled: true,
        trigger: new WorkflowTrigger({
            trigger: {
                case: "manual",
                value: new WorkflowTrigger_Manual(),
            },
            context: new WorkflowTriggerContext({
                context: {
                    case: "projects",
                    value: new WorkflowTriggerContext_Projects({
                        projectIds: [],
                    }),
                },
            }),
        }),
        steps: [
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: "With prompts you can send messages to Ona Agent",
                    },
                },
            }),
            new WorkflowStep({
                step: {
                    case: "task",
                    value: {
                        command: "echo 'with commands you can run shell commands'",
                    },
                },
            }),
        ],
    },
    "automated-dev-environment-setup": {
        id: "automated-dev-environment-setup",
        title: "Automated dev environment setup",
        description:
            "Ensures each repository has a consistent dev environment by analyzing the codebase, updating devcontainer and editor configuration, and opening a pull request with the changes.",
        enabled: true,
        icon: IconFocusAuto,
        iconColor: "text-content-brand-accent-05",
        iconBgColor: "bg-surface-brand-accent-07",
        trigger: new WorkflowTrigger({
            trigger: {
                case: "manual",
                value: new WorkflowTrigger_Manual(),
            },
            context: new WorkflowTriggerContext({
                context: {
                    case: "projects",
                    value: new WorkflowTriggerContext_Projects({
                        projectIds: [],
                    }),
                },
            }),
        }),
        steps: [
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: `Set up a fully working development environment as code for the current environment using the devcontainer-setup skill.

Use the read_skill tool to load the "devcontainer-setup" skill and follow its workflow to:
1. Analyze the repository structure
2. Create devcontainer.json and automations.yaml
3. Rebuild the devcontainer and verify the environment

Do not create any documentation files.`,
                    },
                },
            }),
            new WorkflowStep({
                step: {
                    case: "pullRequest",
                    value: new WorkflowStep_PullRequest({
                        title: "Ona: automated dev environment setup",
                        description:
                            "The PR has been automatically created by Ona Agent to set up a consistent development environment for this repository. Please review the changes and merge if everything looks good.",
                        branch: "ona/automated-dev-environment-setup",
                        draft: true,
                    }),
                },
            }),
        ],
    },
    "cve-mitigation-and-version-updates": {
        id: "cve-mitigation-and-version-updates",
        title: "CVE mitigation and version updates",
        description:
            "Analyzes a specific CVE, determines if the repository is affected, and if so, automatically remediates the vulnerability by updating dependencies, migrating code to new APIs, running tests, and creating a pull request with the complete fix.",
        enabled: true,
        icon: IconShieldCheck3,
        iconColor: "text-content-brand-accent-01",
        iconBgColor: "bg-surface-brand-accent-01",
        trigger: new WorkflowTrigger({
            trigger: {
                case: "manual",
                value: new WorkflowTrigger_Manual(),
            },
            context: new WorkflowTriggerContext({
                context: {
                    case: "projects",
                    value: new WorkflowTriggerContext_Projects({
                        projectIds: [],
                    }),
                },
            }),
        }),
        steps: [
            // Step 1: Research CVE
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: "Research {CVE_ID}: what is the vulnerability, which library/package, what versions are affected, what versions have the fix, and which specific functions or code patterns trigger it. Do not look at repository code yet. Output: one sentence status only.",
                    },
                },
            }),
            // Step 2: Find dependency
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: "Check if this repository has the vulnerable library. Look at dependency files and analyze the full dependency tree including transitive dependencies. Report the version and whether it's direct or transitive. If not found or already patched, say so and skip remaining work. Do not analyze source code yet. Output: one sentence status only.",
                    },
                },
            }),
            // Step 3: Check reachability
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: "If the vulnerable library was found: search source code to find if our code calls the vulnerable functions. Show the call chain. If the library was not found or not reachable, skip this work. Do not modify any files yet. Output: one sentence status only.",
                    },
                },
            }),
            // Step 4: Apply fix
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: "If the vulnerability is reachable: update the dependency to the fixed version, adjust code if APIs changed, and run tests. Otherwise skip this work. Do not create a PR yet. Output: one sentence status only.",
                    },
                },
            }),
            // Step 5: Create PR
            new WorkflowStep({
                step: {
                    case: "pullRequest",
                    value: new WorkflowStep_PullRequest({
                        title: "fix: CVE remediation",
                        description: "Automated CVE remediation by Ona Agent.",
                        branch: "ona/cve-remediation",
                        draft: true,
                    }),
                },
            }),
            // Step 6: Generate report
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: "Generate a security analysis report using all information from previous steps. Format:\n\n**Result:** [Vulnerable/Not Vulnerable/Already Patched] - one line summary\n\n**Impact:** CVSS score and worst-case scenario\n\n**Evidence:** Call chain showing reachability (or why not reachable)\n\n**Fix:** What was changed (if applicable)\n\n**Verification:** Test results (if applicable)\n\nPut the outcome first. Be concise.",
                    },
                },
            }),
        ],
    },
    "add-and-maintain-readmes-and-backstage-yaml": {
        id: "add-and-maintain-readmes-and-backstage-yaml",
        title: " Backstage catalog standardization",
        description:
            "Fetches a Backstage RFC from an external source of truth, analyzes the repository it's launched on, and creates or updates the catalog-info.yaml file to meet the RFC, it validates using the Entity using Backstage Validator CLI, creates a pull request, and adds a comment for what requires manual human input.",
        enabled: true,
        icon: IconFileText,
        iconColor: "text-content-brand-accent-02",
        iconBgColor: "bg-surface-brand-accent-02",
        trigger: new WorkflowTrigger({
            trigger: {
                case: "manual",
                value: new WorkflowTrigger_Manual(),
            },
            context: new WorkflowTriggerContext({
                context: {
                    case: "projects",
                    value: new WorkflowTriggerContext_Projects({
                        projectIds: [],
                    }),
                },
            }),
        }),
        steps: [
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: `Fetch and analyze the Backstage catalog standardization RFC from this Gist: https://gist.github.com/loujaybee/7f8ed30f4dd2dee94a5db019b47cce62

Extract and summarize:

All required fields and their specifications
Acceptable values and formats for each field
Fields that can typically be inferred automatically vs. those requiring human input
Validation rules and compliance requirements

Output a structured summary of the RFC requirements that will guide the subsequent steps.`,
                    },
                },
            }),
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: `Examine this repository to understand its characteristics:

What type of component is this? (service, library, website, or documentation)
What is the primary programming language and technology stack?
What frameworks, databases, or notable dependencies are in use?
Does a catalog-info.yaml file already exist at the repository root? If so, what does it contain?
Based on the README, repository structure, and code, what is the purpose of this component?
Is this component actively maintained? (check recent commit history)
Are there any existing configuration files that provide context (package.json, requirements.txt, etc.)?

Provide a comprehensive analysis that will inform the catalog-info.yaml generation.`,
                    },
                },
            }),
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: `Based on the RFC requirements and repository analysis, generate or update the catalog-info.yaml file:

Create the file with proper Backstage catalog format (apiVersion: backstage.io/v1alpha1, kind: Component)
Populate all fields that can be confidently determined automatically:

metadata.name (derived from repository name)
metadata.description (based on repository purpose)
metadata.tags (technology stack and component category)
spec.type (service/library/website/documentation)
spec.lifecycle (production/experimental/deprecated based on activity and context)


For fields requiring human input (spec.owner, spec.system), add TODO comments with clear guidance on what needs to be provided
Include inline YAML comments explaining automated choices where helpful
Ensure valid YAML syntax

If catalog-info.yaml already exists, preserve any manually-added fields and only update missing or outdated automated fields.`,
                    },
                },
            }),
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: `Install the Backstage entity validator and validate the generated catalog-info.yaml file:

npm install --global @roadiehq/backstage-entity-validator

validate-entity catalog-info.yaml

Review the validation output. If there are any errors, fix them and re-validate until the file passes validation.

Report the validation results.`,
                    },
                },
            }),
            new WorkflowStep({
                step: {
                    case: "pullRequest",
                    value: new WorkflowStep_PullRequest({
                        title: "chore: add Backstage catalog metadata per RFC-2024-023",
                        description: `This PR implements the Backstage catalog standardization requirements defined in RFC-2024-023.

Changes Made:

[Created/Updated] catalog-info.yaml with automated fields
All automated fields populated based on repository analysis
File validated using @roadiehq/backstage-entity-validator

What Was Automated:

Component type, description, and technology tags
Lifecycle determination based on repository activity
Basic metadata derived from repository structure

Requires Manual Review:

spec.owner: Please specify the team responsible for this component
spec.system: Please specify which system/product this component belongs to
Review all automated fields for accuracy

Validation: ✅ Passed Backstage entity validation
Please review the automated fields and complete the manual items before merging.`,
                        branch: "ona/backstage-catalog-standardization",
                        draft: true,
                    }),
                },
            }),
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: `Add a comment to the pull request highlighting what requires human attention:

⚠️ Manual Action Required
While most of the catalog metadata has been populated automatically, the following fields require your input:
Required Manual Fields:

spec.owner (Line XX)

Current: TODO: Specify team
Action needed: Replace with your team identifier (e.g., team:platform-engineering)
See team registry for valid team names

spec.system (Line XX)

Current: TODO: Specify system
Action needed: Specify which product/system this component belongs to
See system definitions or contact Platform Engineering to create a new system

Please Review:

Verify the component description accurately reflects the purpose
Confirm the technology tags are complete
Ensure the lifecycle state (production/experimental/deprecated) is correct

Once you've completed these items, this component will be fully compliant with RFC-2024-023.`,
                    },
                },
            }),
        ],
    },
    "code-review": {
        id: "code-review",
        title: "Code review",
        description:
            "Automatically reviews pull requests for code quality, security issues, and test coverage. Posts inline comments for medium to high severity issues and optionally approves clean PRs.",
        enabled: true,
        icon: IconCodeAnalyze,
        iconColor: "text-content-brand-accent-06",
        iconBgColor: "bg-surface-brand-accent-03",
        trigger: new WorkflowTrigger({
            trigger: {
                case: "pullRequest",
                value: new WorkflowTrigger_PullRequest({
                    events: [
                        WorkflowTrigger_PullRequestEvent.OPENED,
                        WorkflowTrigger_PullRequestEvent.READY_FOR_REVIEW,
                    ],
                }),
            },
            context: new WorkflowTriggerContext({
                context: {
                    case: "projects",
                    value: new WorkflowTriggerContext_Projects({
                        projectIds: [],
                    }),
                },
            }),
        }),
        steps: [
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: `CODE REVIEW AGENT FOR ONA

1. PERSONA & ROLE

You are an expert software engineer performing code review for Ona. Your mission: provide high-value, actionable feedback on medium to high severity issues only.

Core Principles:
- Be helpful, not noisy. Only raise fair, actionable concerns that genuinely improve code.
- Focus exclusively on what changed in this PR (the diff).
- Work with available context. If tools fail, continue with what you have.
- Don't comment on style preferences. Only flag significant inconsistencies with existing codebase patterns.
- Treat generated code fairly if it's appropriate and contextually correct.

2. CONTEXT GATHERING

Gather context systematically, but continue even if some sources fail:

2.1 Identify & Verify PR
- Get current git branch and extract PR number
- Fetch PR state (open/closed/merged)
- EXIT IMMEDIATELY if closed or merged

2.2 Gather Available Context
Try to collect, but continue if any fail:
- PR description: problem, solution, requirements, breaking changes
- Commit messages: progression, decisions, iterations
- Linked issues: explore available tools to retrieve more context from linked issues
- Existing comments: what's already been raised, resolved status
- Code diff: full changes against default branch
- Existing codebase patterns: naming, error handling, testing conventions
- Repo coding guidelines, agent rules, and skills (available in system prompt context)

2.3 Repo-Specific Review Rules
- Check if a \`code-review\` skill exists using read_skill tool
- If found, apply those rules in addition to standard review dimensions
- Repo-specific rules take precedence for severity assessment

CRITICAL: Only review code in the diff. Never comment on:
- Pre-existing unchanged code
- Legacy issues outside PR scope
- General codebase patterns not touched by this PR

3. UNDERSTANDING & ANALYSIS

Synthesize context to build a mental model:
- What is this PR trying to achieve?
- What are the key technical changes and risks?
- What patterns exist in the codebase?
- What constraints or trade-offs are mentioned?

Focus areas based on context:
- Critical paths and core logic changes
- Security-sensitive code (auth, validation, data handling)
- Performance-critical sections
- Public APIs and interfaces
- Error handling and edge cases
- Test coverage and quality
- Consistency with existing patterns

4. SELF-REFLECTION & VALIDATION

Before flagging any issue, ask:
- Is this in the diff (code actually changed)?
- Is this intentional per PR description or linked issues?
- Does existing codebase use similar approaches?
- Has this been discussed by other reviewers?
- Could missing context make this look wrong when it's right?
- Is this medium/high severity or just a nit?
- Is this actionable within PR scope?
- Is this about substance or style preference?
- Would I want this feedback if I were the author?

Cross-reference against available context:
- PR description justification
- Linked issue decisions
- Similar patterns in existing code
- Other reviewer comments

Severity Assessment:
- High: Will likely cause bugs, security issues, or major problems
- Medium: Could lead to bugs, maintenance issues, or moderate concerns
- Skip: Low severity, nits, style preferences, duplicates, pre-existing issues, uncertain due to limited context

When uncertain, skip rather than create noise.

5. CODE REVIEW DIMENSIONS

Review ONLY changed code across these areas:

5.1 Correctness & Quality
- Error/exception handling completeness
- Resource management (connections, files, memory)
- Type safety and null safety
- Logging consistency
- Edge case handling

5.2 Security
- Input validation and sanitization
- Access control and authorization
- SQL injection, XSS, CSRF prevention
- Secret management (no hardcoded secrets)
- Dependency vulnerabilities

5.3 Performance & Reliability
- Memory leaks and allocation patterns
- Concurrency correctness
- N+1 queries or inefficient operations
- Retry logic and timeout handling
- Data consistency

5.4 Architecture & Maintainability
- Component boundaries and coupling
- Interface design
- Code clarity and complexity
- Documentation for public APIs

5.5 Consistency with Existing Codebase
Only flag if inconsistency is significant:
- Does new code follow same patterns as existing similar code?
- Same error handling, naming, logging approaches?
- Same code organization structure?

Don't comment on style preferences, only significant inconsistencies causing confusion.

5.6 Generated Code Assessment
If code appears generated (repetitive, boilerplate, generic):
- Verify it's appropriate and adapted to specific use case
- Check it follows project patterns, not generic templates
- Flag if manipulated or illogical
- Don't penalize if correct and contextually appropriate

5.7 Testing (CRITICAL)

Coverage:
- Are new functions, methods, branches tested?
- Edge cases, error scenarios covered?
- Missing tests for significant functionality?
- Coverage notably low (under 70%)?

Quality:
- Do tests verify intended behavior?
- Meaningful assertions (not just "no crash")?
- Test names descriptive and clear?
- Tests isolated and independent?

Common Issues:
- Over-mocking: testing mock behavior instead of real logic
- Type "any" abuse: bypassing type checking
- Mock core business logic instead of testing it
- Test implementation details instead of behavior
- Missing negative/error tests
- Arbitrary sleeps instead of proper synchronization
- Tests inconsistent with existing test patterns

Severity:
- High: Missing tests for critical paths, "any" type abuse, mocking core logic
- Medium: Low coverage, tests not verifying behavior, excessive mocking

6. VALIDATE FINDINGS

Before posting, verify each finding:
- In the diff? (not pre-existing code)
- Relevant to PR goals?
- Not duplicate of existing comment?
- Not already resolved?
- Actionable with concrete solution?
- Helpful and fair feedback?
- Concise (2-4 sentences)?

If you couldn't fetch existing comments, skip duplication/resolution checks.

7. POST COMMENTS

7.1 Final PR Status Check
- Re-fetch PR state before posting
- EXIT if closed/merged
- Continue cautiously if check fails

7.2 Comment Placement
- ALWAYS prefer inline diff comments on specific lines
- Use general PR comments only for: cross-cutting concerns, architectural feedback, final summary

7.3 Comment Format
[Brief issue in 1 sentence]
[Impact in 1 sentence]
[Suggested fix with code when possible]

Use code suggestions:
\`\`\`suggestion
// improved code here
\`\`\`

7.4 Never Do
- Mark PR as "Changes Requested" or "Approved"
- Post duplicates, nits, or style comments
- Comment on resolved issues
- Write long explanations
- Comment on pre-existing code
- Produce text output (only use tools)

7.5 Final Summary Comment
After posting individual comments (or if none found):

If issues found:
"Reviewed this PR and found [N] areas that need attention. Please see inline comments for details."

If no issues after thorough review:
"Reviewed the changes. Implementation looks solid - good code quality, appropriate test coverage, follows established patterns. No significant concerns."

Acknowledge specific strengths when appropriate (test quality, documentation, best practices).

Only post positive summary if you genuinely found no medium/high issues after complete review.

8. EXECUTION WORKFLOW

1. Identify PR from git branch
2. Check PR status - EXIT if closed/merged
3. Gather context (continue if sources fail)
4. Understand and establish focus areas
5. Review code systematically (diff only)
6. Validate each finding thoroughly
7. Re-check PR status - EXIT if closed/merged
8. Post inline comments and final summary
9. Exit silently (no text output)

9. SUCCESS CRITERIA

- Gathered available context without failing on missing tools
- Focused only on diff, not pre-existing code
- Evaluated test quality and consistency thoroughly
- Only reported medium/high severity issues
- Avoided duplicates, nits, style preferences
- Provided concise, actionable feedback with suggestions
- Verified PR status before posting
- Posted final summary (issues or positive acknowledgment)
- Been helpful, fair, and constructive
- Produced zero text output (only tool calls)

REMEMBER: Quality over quantity. Understand before critiquing. Focus on what changed. Be helpful and fair. Check consistency with existing patterns, not arbitrary style. Treat generated code fairly. Always conclude with summary. Work with available context - never fail due to missing pieces.`,
                    },
                },
            }),
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: `If you found no significant concerns in your review, add a "ona-approved" label to the pull request. You have my explicit permission to add this label if you have no concerns about this code getting merged.`,
                    },
                },
            }),
        ],
    },
    "generate-agents-md": {
        id: "generate-agents-md",
        title: "Add optimized Agents.md",
        description:
            "Analyzes repositories to generate or update AGENTS.md files with project-specific instructions for AI coding agents. Creates high-quality documentation covering setup commands, testing, code style, and PR guidelines.",
        enabled: true,
        icon: IconRobot,
        iconColor: "text-content-brand-accent-04",
        iconBgColor: "bg-surface-brand-accent-04",
        trigger: new WorkflowTrigger({
            trigger: {
                case: "manual",
                value: new WorkflowTrigger_Manual(),
            },
            context: new WorkflowTriggerContext({
                context: {
                    case: "projects",
                    value: new WorkflowTriggerContext_Projects({
                        projectIds: [],
                    }),
                },
            }),
        }),
        steps: [
            new WorkflowStep({
                step: {
                    case: "agent",
                    value: {
                        prompt: `Create or update AGENTS.md (https://agents.md) - a README that helps AI agents work on this repo.

**Include:**
- Setup commands (install, build, dev server) from package.json/Makefile/similar
- Test commands with examples for running specific tests
- Lint/format commands to run before committing
- PR requirements: branch naming, commit format, required CI checks
- Key directories with brief descriptions

**Rules:**
- Only document commands that exist - verify against source files
- Keep it scannable, not prose
- Preserve valid existing content when updating
- For monorepos: include root commands and per-package instructions
- Omit sections with no information`,
                    },
                },
            }),
            new WorkflowStep({
                step: {
                    case: "pullRequest",
                    value: new WorkflowStep_PullRequest({
                        title: "chore: add AGENTS.md",
                        description: `Adds/Improves AGENTS.md to set up this repo for success with Ona Agent.

AGENTS.md provides project-specific instructions (setup, test, lint commands, PR guidelines) that help Ona work effectively on this codebase.

## Review checklist
- [ ] Commands are accurate
- [ ] Code style section matches project conventions
- [ ] PR guidelines match team expectations

See https://ona.com/docs/ona/agents-md and https://agents.md for background.`,
                        branch: "ona/agents-md",
                        draft: true,
                    }),
                },
            }),
        ],
    },
} as const;

export type AutomationTemplateId = keyof typeof AUTOMATION_TEMPLATES_INTERNAL;

export type AutomationTemplate = {
    id: AutomationTemplateId;
    title: string;
    description: string;
    icon: React.FC<CentralIconBaseProps>;
    iconColor: string;
    iconBgColor: string;
    steps: readonly WorkflowStep[];
    trigger: WorkflowTrigger;
    enabled: boolean;
};

export type TemplateGroup = {
    id: string;
    label?: string;
    templates: Record<string, AutomationTemplate>;
};

const TEMPLATE_GROUPS = {
    "launch-use-cases": {
        id: "launch-use-cases",
        label: undefined,
        templates: {
            "automated-dev-environment-setup": AUTOMATION_TEMPLATES_INTERNAL["automated-dev-environment-setup"],
            "cve-mitigation-and-version-updates": AUTOMATION_TEMPLATES_INTERNAL["cve-mitigation-and-version-updates"],
            "add-and-maintain-readmes-and-backstage-yaml":
                AUTOMATION_TEMPLATES_INTERNAL["add-and-maintain-readmes-and-backstage-yaml"],
            "code-review": AUTOMATION_TEMPLATES_INTERNAL["code-review"],
            "generate-agents-md": AUTOMATION_TEMPLATES_INTERNAL["generate-agents-md"],
        },
    },
    "from-scratch": {
        id: "from-scratch",
        label: "Or",
        templates: {
            "start-from-scratch": AUTOMATION_TEMPLATES_INTERNAL["start-from-scratch"],
        },
    },
};

export type TemplateGroupId = keyof typeof TEMPLATE_GROUPS;

export const getTemplateById = (id: string): AutomationTemplate | undefined =>
    AUTOMATION_TEMPLATES_INTERNAL[id as AutomationTemplateId];

/**
 * Returns template groups with templates filtered by the given capabilities.
 * Templates with a PR trigger are excluded when PullRequestTrigger capability is absent.
 * Groups that end up empty after filtering are excluded.
 */
export const getTemplateGroups = (capabilities: Set<AutomationsCapabilities>): TemplateGroup[] => {
    const hasPRTrigger = capabilities.has(AutomationsCapabilities.PullRequestTrigger);

    return Object.values(TEMPLATE_GROUPS)
        .map((group) => {
            if (hasPRTrigger) {
                return group;
            }

            const filtered = Object.fromEntries(
                Object.entries(group.templates).filter(
                    ([, template]) => template.trigger.trigger.case !== "pullRequest",
                ),
            );

            return { ...group, templates: filtered };
        })
        .filter((group) => Object.keys(group.templates).length > 0);
};

export const newWorkflowFromTemplate = (template: AutomationTemplate, userId: string) => {
    return new Workflow({
        metadata: {
            name: template.title,
            description: template.description,
            executor: {
                id: userId,
                principal: Principal.USER,
            },
        },
        spec: {
            triggers: [template.trigger],
            action: new WorkflowAction({
                limits: new WorkflowAction_Limits({
                    maxParallel: DEFAULT_WORKFLOW_LIMITS.maxParallel,
                    maxTotal: DEFAULT_WORKFLOW_LIMITS.maxTotal,
                }),
                steps: [...template.steps],
            }),
        },
    });
};
